import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
} from "@mui/material";
import ProductTable from "./Product";
import {
  primaryColor,
  primaryColorDark,
  buttonColor,
  buttonHoverColor,
  fontfamily,
} from "../../Util/UtilColorPalate";
import { onboardingCreateApi } from "../../Util/UtilAPi";
import UtilToastMessages from "../../Util/UtilToastMessages";
import { ORG } from "../Data";

const currencySymbols = {
  INR: "₹",
  USD: "$",
  ZAR: "R",
};

const currencies = [
  { value: "INR - ₹", label: "₹ Indian Rupee" },
  { value: "ZAR - R", label: "R South African Rand" },
  { value: "USD - $", label: "$ US Dollar" },
];

// Products that should never be shown or selectable
const EXCLUDED_PRODUCTS = ["new domain"];

// Notice shown above the SMS packages
const SMS_PROVIDER_NOTE =
  "You need to have an account with one of our associated SMS providers. We'll assist you with the onboarding process.";

// Decide which section a non-bundle product belongs to.
// Adjust this if your API has a dedicated category field.
const getCategory = (item) => {
  if (item.bundle === true) return "Bundles";
  const text = `${item.category || ""} ${item.productcode || ""} ${
    item.name || ""
  }`.toLowerCase();
  return text.includes("sms") ? "SMS" : "License";
};

// The CRM bundle is always selected and cannot be unticked.
// Adjust the match if your API has a better identifier (e.g. productcode).
const isCrmBundle = (item) =>
  item.bundle === true && (item.name || "").toLowerCase().includes("crm");

const ProductSelection = ({
  onNext,
  productSelection,
  onPrevious,
  companyCurrency,
  checkoutDetails,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSnackbarOpen, setSnackbarOpen] = useState(false);
  const [toastColor, setToastColor] = useState();
  const [toastmessage, setToastmessage] = useState("");

  // { [recordid]: quantity } for every ticked row (bundles, SMS, license)
  const [selected, setSelected] = useState({});
  const [wantsDomain, setWantsDomain] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [currency, setCurrency] = useState(companyCurrency || "INR - ₹");
  const [currentCurrencyRate, setCurrentCurrencyRate] = useState(1);
  const [currencyCode, setCurrencyCode] = useState("");

  const currencySymbol = currencySymbols[currency.split(" - ")[0]] || currency;

  const showToast = (success, message) => {
    setToastColor(success);
    setToastmessage(message);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // ---- Group items into the three sections ----
  const sections = useMemo(() => {
    const groups = { Bundles: [], SMS: [], License: [] };
     allItems
  .filter(
    (item) =>
      item.division?.some((d) => d.name === ORG) &&
      item.subdevision?.some((s) =>
        ["Onboarding", "Specials"].includes(s.name)
      )
  )
      .forEach((item) => groups[getCategory(item)].push(item));
    return groups;
  }, [allItems]);

  // ---- Derived values sent to the API / next step ----
  const pickedItems = useMemo(
    () =>
      allItems
        .filter((item) => selected[item.recordid] !== undefined)
        .map((item) => ({
          ...item,
          qty:
            item.bundle === true
              ? 1
              : Math.max(parseInt(selected[item.recordid], 10) || 1, 1),
        })),
    [allItems, selected]
  );

  const selectedItems = useMemo(
    () => ({
      selection:
        pickedItems.length > 0 && pickedItems.every((i) => i.bundle === true)
          ? "package"
          : "product",
      selectedProducts: pickedItems.map((item) => ({
        discount: 0,
        quantity: item.qty,
        name: item.name,
        product: Number(item?.recordid?.slice(5)),
      })),
    }),
    [pickedItems]
  );

  const productDetails = useMemo(
    () =>
      pickedItems.map((item) => ({
        quantity: item.qty,
        name: item.name,
        product: Number(item?.recordid?.slice(5)),
        price: item.unitpriceinclvat,
        selection: item.bundle === true ? "package" : "product",
        currency: currencySymbol,
        productcode: item.productcode,
      })),
    [pickedItems, currencySymbol]
  );

  // ---- Quantity validation: { [recordid]: "error message" } ----
  const quantityErrors = useMemo(() => {
    const errors = {};
    allItems.forEach((item) => {
      const value = selected[item.recordid];
      if (value === undefined || item.bundle === true) return; // not ticked / locked qty
      if (value === "" || value === null) {
        errors[item.recordid] = "Quantity is required";
      } else if (isNaN(parseInt(value, 10)) || parseInt(value, 10) < 1) {
        errors[item.recordid] = "Minimum quantity is 1";
      }
    });
    return errors;
  }, [allItems, selected]);

  // ---- Table handlers ----
  const handleToggle = (item) => {
    if (isCrmBundle(item)) return; // locked
    setSelected((prev) => {
      const next = { ...prev };
      if (next[item.recordid] !== undefined) {
        delete next[item.recordid]; // unselect -> quantity resets
      } else {
        next[item.recordid] = 1;
      }
      return next;
    });
  };

  // While typing, allow an empty string
  const handleQuantityChange = (item, value) => {
    setSelected((prev) => ({ ...prev, [item.recordid]: value }));
  };

  // On blur, normalise valid numbers (e.g. "007" -> 7).
  // Empty / invalid values are kept so the validation error stays visible.
  const handleQuantityBlur = (item, value) => {
    const qty = parseInt(value, 10);
    if (!isNaN(qty) && qty >= 1) {
      setSelected((prev) => ({ ...prev, [item.recordid]: qty }));
    }
  };

  // ---- Validation ----
  const checkPrerequisites = () => {
    const selectedProductCodes = productDetails.map((item) => item.productcode);
    for (const product of productDetails) {
      const selectedProductData = allItems.find(
        (p) => p.productcode === product.productcode
      );
      if (selectedProductData?.prerequisite?.length > 0) {
        const prerequisiteCodes = selectedProductData.prerequisite.map(
          (prereq) => prereq.name
        );
        const isPrerequisiteMet = prerequisiteCodes.some((code) =>
          selectedProductCodes.includes(code)
        );
        if (!isPrerequisiteMet) {
          return {
            isValid: false,
            message: `The product "${selectedProductData.name}" requires at least one of the following prerequisites: ${prerequisiteCodes.join(
              ", "
            )}.`,
          };
        }
      }
    }
    return { isValid: true, message: "" };
  };

  const addToCart = async () => {
    const leadId = localStorage.getItem("leadid");
    setIsSubmitting(true);
    const payload = {
      action: "deal",
      classification: ORG,
      leadid: leadId,
      domain: "",
      isocurrency: currency,
      data: selectedItems.selectedProducts,
    };

    try {
      const response = await onboardingCreateApi(payload);
      if (response.statusCode === 201) {
        localStorage.setItem("deal", response.data.deal.toString());
        onNext({
          selectedItems,
          productDetails,
          includeDomain: false,
          currency,
          paymentCurrency: currency,
        });
      } else if (response.statusCode === 400) {
        showToast(response.success, response.message);
      }
    } catch (err) {
      showToast(err?.response?.data?.success, err?.response?.data?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (selectedItems.selectedProducts.length === 0) {
      showToast(false, "Please select at least one bundle, SMS or license");
      return;
    }

    const invalidItem = pickedItems.find((i) => quantityErrors[i.recordid]);
    if (invalidItem) {
      showToast(
        false,
        `Please enter a valid quantity for "${invalidItem.name}"`
      );
      return;
    }

    const prerequisiteCheck = checkPrerequisites();
    if (!prerequisiteCheck.isValid) {
      showToast(false, prerequisiteCheck.message);
      return;
    }

    if (wantsDomain) {
      // Domain details are collected in the next step
      onNext({
        selectedItems,
        productDetails,
        includeDomain: true,
        currency,
        paymentCurrency: currency,
      });
    } else {
      addToCart();
    }
  };

  // ---- Prefill from previous step ----
  useEffect(() => {
    const items = productSelection || [];
    const prefilled = {};
    items.forEach((item) => {
      if (item?.product?.recordid) {
        prefilled[item.product.recordid] = item.quantity || 1;
      }
    });
    setSelected(prefilled);

    if (checkoutDetails?.currency) {
      setCurrency(checkoutDetails.currency);
    }
  }, [productSelection]);

  // ---- Always keep the CRM bundle ticked ----
  useEffect(() => {
    const crmItems = sections.Bundles.filter(isCrmBundle);
    if (crmItems.length === 0) return;
    setSelected((prev) => {
      const missing = crmItems.filter(
        (item) => prev[item.recordid] === undefined
      );
      if (missing.length === 0) return prev; // no change, no re-render
      const next = { ...prev };
      missing.forEach((item) => {
        next[item.recordid] = 1;
      });
      return next;
    });
  }, [sections, selected]);

  // ---- Fetch bundles, SMS and licenses in one call ----
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await onboardingCreateApi({ action: "product" });

      if (response.statusCode === 200 || response.statusCode === 201) {
        const currencyKey = Object.keys(currencySymbols).find(
          (key) => currencySymbols[key] === response.currency
        );
        setCurrencyCode(currencyKey);

        setAllItems(
          response.data
            .filter(
              (item) =>
                !EXCLUDED_PRODUCTS.includes(
                  (item.name || "").trim().toLowerCase()
                )
            )
            .map((item) => ({
              ...item,
              unitpriceinclvat: item.unitpriceinclvat * currentCurrencyRate,
              currency: currencySymbol,
            }))
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentCurrencyRate, companyCurrency]);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await fetch(
          `https://open.er-api.com/v6/latest/${currencyCode}`
        );
        const data = await response.json();
        if (data.result === "success") {
          setCurrentCurrencyRate(
            data.rates[currency.split(" - ")[0].trim()] || 1
          );
        } else {
          console.error("Error fetching rates:", data);
        }
      } catch (error) {
        console.error("Fetch error:", error);
      }
    };
    if (currencyCode) fetchRates();
  }, [currency, currencyCode]);

  const tableProps = {
    selected,
    loading: isLoading,
    quantityErrors,
    onToggle: handleToggle,
    onQuantityChange: handleQuantityChange,
    onQuantityBlur: handleQuantityBlur,
  };

  return (
    <>
      <Box sx={{ padding: "20px" }}>
        <TextField
          select
          label="Select Payment Currency"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          fullWidth
          margin="normal"
          size="small"
          sx={{
            fontFamily: `${fontfamily} !important`,
            "& .MuiOutlinedInput-root": {
              fontFamily: `${fontfamily} !important`,
              "&.Mui-focused fieldset": { borderColor: primaryColor },
            },
            "& .MuiInputLabel-root": {
              fontFamily: `${fontfamily} !important`,
              "&.Mui-focused": { color: primaryColor },
            },
          }}
        >
          {currencies.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>

        <ProductTable
          title="Bundles"
          items={sections.Bundles}
          lockQuantity
          isItemLocked={isCrmBundle}
          {...tableProps}
        />

     
        <ProductTable
          title="Add on Products"
          items={sections.SMS}
          {...tableProps}
        />
           {sections.SMS.length > 0 && (
          <Alert
            severity="info"
            sx={{
              mt: 2,
              py: 0,
              px: 1.5,
              fontFamily: fontfamily,
              fontSize: "0.8rem",
              alignItems: "center",
              "& .MuiAlert-icon": { py: 0.5, mr: 1, fontSize: 18 },
              "& .MuiAlert-message": {
                fontFamily: fontfamily,
                py: 0.5,
                lineHeight: 1.3,
              },
            }}
          >
            {SMS_PROVIDER_NOTE}
          </Alert>
        )}

        <ProductTable title="License" items={sections.License} {...tableProps} />

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={wantsDomain}
                onChange={(e) => setWantsDomain(e.target.checked)}
                sx={{
                  color: primaryColor,
                  "&.Mui-checked": { color: primaryColorDark },
                }}
              />
            }
            label="Do you want to purchase new domain"
            sx={{
              "& .MuiFormControlLabel-label": {
                fontFamily: fontfamily,
                color: primaryColorDark,
              },
            }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "end" }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{
              mt: 2,
              px: 4,
              py: 1,
              ml: 1,
              fontFamily: fontfamily,
              backgroundColor: buttonColor,
              "&:hover": { backgroundColor: buttonHoverColor },
              "&:disabled": { backgroundColor: "grey.300", cursor: "not-allowed" },
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress
                  size={10}
                  color="inherit"
                  style={{ marginRight: 8 }}
                />
                Next
              </>
            ) : (
              "Next"
            )}
          </Button>
        </Box>
      </Box>

      <UtilToastMessages
        isOpen={isSnackbarOpen}
        handleClose={handleSnackbarClose}
        data={toastmessage}
        severity={toastColor}
      />
    </>
  );
};

export default ProductSelection;